import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { rewardsService } from "../services/firebaseConfig";
import { UserVoucher } from "../types";

interface VoucherContextValue {
  activeFreeDeliveryVouchers: UserVoucher[];
  isLoading: boolean;
  error: Error | null;
  prefetchVouchers: () => Promise<void>;
}

const VoucherContext = createContext<VoucherContextValue | undefined>(undefined);

/**
 * VoucherContext Provider
 * Fetches user vouchers once and shares across all components
 * Optimized with React Query caching and prefetching support
 */
export const VoucherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: userVouchers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["userVouchers", currentUser?.uid],
    queryFn: async () => {
      if (!currentUser?.uid) {
        return [];
      }
      return await rewardsService.getUserVouchers(currentUser.uid);
    },
    enabled: !!currentUser?.uid,
    staleTime: 10 * 60 * 1000, // 10 minutes (vouchers don't change frequently)
    gcTime: 30 * 60 * 1000, // 30 minutes (keep in cache longer)
    refetchOnWindowFocus: false, // Reduce unnecessary refetches
    refetchOnMount: false, // Use cached data if fresh
  });

  // Filter for active free delivery vouchers (memoized)
  const activeFreeDeliveryVouchers = useMemo(() => {
    return userVouchers.filter((userVoucher: UserVoucher) => {
      const voucher = userVoucher.voucher;
      
      // Must be free delivery type
      if (voucher.discountType !== "free_delivery") {
        return false;
      }

      // Must not be used
      if (userVoucher.isUsed) {
        return false;
      }

      // Must be active
      if (!voucher.isActive) {
        return false;
      }

      // Check expiration
      const now = new Date();
      const expiresAt = userVoucher.expiresAt?.toDate?.() || voucher.expiresAt?.toDate?.() || null;
      if (expiresAt && expiresAt < now) {
        return false;
      }

      // Check start date if exists
      const startDate = voucher.startDate?.toDate?.() || null;
      if (startDate && startDate > now) {
        return false;
      }

      return true;
    });
  }, [userVouchers]);

  // Prefetch vouchers function for prioritized loading
  const prefetchVouchers = useCallback(async () => {
    if (!currentUser?.uid) {
      return;
    }

    await queryClient.prefetchQuery({
      queryKey: ["userVouchers", currentUser.uid],
      queryFn: async () => {
        return await rewardsService.getUserVouchers(currentUser.uid);
      },
      staleTime: 10 * 60 * 1000,
    });
  }, [currentUser?.uid, queryClient]);

  const value: VoucherContextValue = useMemo(
    () => ({
      activeFreeDeliveryVouchers,
      isLoading,
      error: error as Error | null,
      prefetchVouchers,
    }),
    [activeFreeDeliveryVouchers, isLoading, error, prefetchVouchers]
  );

  return <VoucherContext.Provider value={value}>{children}</VoucherContext.Provider>;
};

/**
 * Hook to access voucher context
 * Throws error if used outside VoucherProvider
 */
export function useVoucherContext(): VoucherContextValue {
  const context = useContext(VoucherContext);
  if (context === undefined) {
    throw new Error("useVoucherContext must be used within a VoucherProvider");
  }
  return context;
}

